<template>
  <div>
    <!-- Modal Structure -->
    <div
      v-if="show"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      aria-labelledby="errorModalLabel"
      style="background: rgba(0, 0, 0, 0.8); z-index: 9999;"
    >
      <div class="modal-dialog modal-md" role="document">
        <div class="modal-content bg-dark text-light">
          <div class="modal-header border-bottom border-secondary">
            <h5 class="modal-title" id="errorModalLabel">
              <i :class="errorIcon" class="mr-2"></i>
              {{ errorTitle }}
            </h5>
            <button 
              type="button" 
              class="close text-light" 
              @click="closeModal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="error-content">
              <p class="error-message">{{ errorMessage }}</p>
              <div v-if="errorDetails" class="error-details mt-3">
                <small class="text-muted">
                  <strong>Details:</strong> {{ errorDetails }}
                </small>
              </div>
              <div v-if="suggestions.length > 0" class="error-suggestions mt-3">
                <strong>Suggestions:</strong>
                <ul class="mt-2">
                  <li v-for="(suggestion, index) in suggestions" :key="index">
                    {{ suggestion }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top border-secondary">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Close
            </button>
            <button 
              v-if="retryAction" 
              type="button" 
              class="btn btn-primary" 
              @click="retry"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ErrorModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    error: {
      type: Object,
      default: () => ({})
    },
    retryAction: {
      type: Function,
      default: null
    }
  },
  computed: {
    errorTitle() {
      const status = this.error.status || this.error.response?.status;
      switch (status) {
        case 403:
          return "Access Denied";
        case 404:
          return "Resource Not Found";
        case 401:
          return "Authentication Required";
        case 500:
          return "Server Error";
        default:
          return "Error";
      }
    },
    errorMessage() {
      const status = this.error.status || this.error.response?.status;
      const message = this.error.message || this.error.response?.data?.message;
      
      switch (status) {
        case 403:
          return message || "You don't have the required permissions to perform this action.";
        case 404:
          return message || "The requested resource was not found.";
        case 401:
          return message || "Please log in to access this resource.";
        case 500:
          return message || "An internal server error occurred. Please try again later.";
        default:
          return message || "An unexpected error occurred.";
      }
    },
    errorDetails() {
      return this.error.response?.data?.details || this.error.details;
    },
    errorIcon() {
      const status = this.error.status || this.error.response?.status;
      switch (status) {
        case 403:
          return "tim-icons icon-lock-circle text-warning";
        case 404:
          return "tim-icons icon-chart-pie-36 text-info";
        case 401:
          return "tim-icons icon-single-02 text-danger";
        case 500:
          return "tim-icons icon-alert-circle-exc text-danger";
        default:
          return "tim-icons icon-alert-circle-exc text-warning";
      }
    },
    suggestions() {
      const status = this.error.status || this.error.response?.status;
      const suggestions = [];
      
      switch (status) {
        case 403:
          suggestions.push("Contact your administrator to request the necessary permissions.");
          suggestions.push("Check if you're logged in with the correct account.");
          suggestions.push("Verify that you have access to the specific app or resource.");
          break;
        case 404:
          suggestions.push("Check if the URL is correct.");
          suggestions.push("The resource may have been moved or deleted.");
          suggestions.push("Try refreshing the page or navigating back.");
          break;
        case 401:
          suggestions.push("Please log out and log back in.");
          suggestions.push("Check if your session has expired.");
          break;
        case 500:
          suggestions.push("Try again in a few moments.");
          suggestions.push("Contact support if the problem persists.");
          break;
      }
      
      return suggestions;
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    retry() {
      if (this.retryAction) {
        this.retryAction();
      }
      this.closeModal();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./styles/modal_elements.scss";

.error-content {
  .error-message {
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 0;
    color: white;
  }
  
  .error-details {
    background-color: rgba(255, 255, 255, 0.5);
    padding: 0.75rem;
    border-radius: 0.375rem;
    border-left: 3px solid #6c757d;
  }
  
  .error-suggestions {
    background-color: rgba(255, 193, 7, 0.1);
    padding: 0.75rem;
    border-radius: 0.375rem;
    border-left: 3px solid #ffc107;
    
    ul {
      margin-bottom: 0;
      padding-left: 1.25rem;
      
      li {
        margin-bottom: 0.25rem;
        
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

.modal-dialog {
  max-width: 500px;
}

.close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  opacity: 0.5;
  
  &:hover {
    opacity: 0.75;
  }
}
</style> 